import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchLanguageCatalog, type LangEntry } from '../lib/api'
import { useUiStore } from '../lib/uiStore'
import { Icon } from './Icon'

type Props = {
  compact?: boolean
}

type Row = {
  code: string
  name_native: string
  name_en: string
  ui_pack: boolean
  ui_tier: 'stable' | 'beta' | 'none'
  region?: string | null
}

function baseCode(code: string): string {
  return code.split('-')[0]?.toLowerCase() || code.toLowerCase()
}

/** Prefer base language; keep one regional only if no base entry exists. */
function collapseLocales(list: LangEntry[]): Row[] {
  const byBase = new Map<string, LangEntry[]>()
  for (const l of list) {
    const b = baseCode(l.code)
    const arr = byBase.get(b) || []
    arr.push(l)
    byBase.set(b, arr)
  }
  const out: Row[] = []
  for (const [, group] of byBase) {
    const exact = group.find((g) => !g.code.includes('-') && !g.code.includes('_'))
    const pick = exact || group.find((g) => g.ui_pack) || group[0]
    if (!pick) continue
    const region =
      !exact && pick.region
        ? pick.region
        : !exact && pick.code.includes('-')
          ? pick.code.split('-').slice(1).join('-')
          : null
    const tier = (pick.ui_tier as Row['ui_tier']) || (pick.ui_pack ? 'beta' : 'none')
    out.push({
      code: pick.code,
      name_native: pick.name_native,
      name_en: pick.name_en,
      ui_pack: Boolean(pick.ui_pack),
      ui_tier: tier,
      region,
    })
  }
  out.sort((a, b) => a.name_en.localeCompare(b.name_en, 'en', { sensitivity: 'base' }))
  return out
}

function useIsNarrow(maxPx = 759): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${maxPx}px)`).matches : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxPx}px)`)
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [maxPx])
  return narrow
}

export function LanguageMenu({ compact = false }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const locale = useUiStore((s) => s.locale)
  const catalogCode = useUiStore((s) => s.catalogCode)
  const setLocale = useUiStore((s) => s.setLocale)
  const sheet = useIsNarrow(759)

  const { data, isError, isLoading } = useQuery({
    queryKey: ['i18n-languages'],
    queryFn: fetchLanguageCatalog,
    staleTime: 60 * 60 * 1000,
  })

  const collapsed = useMemo(() => collapseLocales(data?.languages ?? []), [data])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return collapsed.slice(0, 100)
    return collapsed
      .filter((l) => {
        const hay = `${l.code} ${l.name_en} ${l.name_native} ${l.region || ''}`.toLowerCase()
        return hay.includes(needle)
      })
      .slice(0, 100)
  }, [collapsed, q])

  const activeCode = (catalogCode || locale).toUpperCase().split('-')[0]

  useEffect(() => {
    if (!open || !sheet) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open, sheet])

  return (
    <div className={`lang-menu${compact ? ' lang-menu--compact' : ''}${sheet ? ' lang-menu--sheet' : ''}`}>
      <button
        type="button"
        className={compact ? 'mail-pill mail-pill-icon' : 'btn btn-ghost'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={t('shell.language')}
      >
        {compact ? (
          <>
            <Icon name="language" size={18} />
            <span>{activeCode}</span>
          </>
        ) : (
          `${t('shell.language')}: ${activeCode}`
        )}
      </button>
      {open && (
        <div
          className={`lang-popover card${sheet ? ' lang-popover--sheet' : ''}`}
          role="dialog"
          aria-modal={sheet || undefined}
          aria-label={t('shell.languageCatalog')}
        >
          {sheet ? (
            <div className="lang-sheet-bar">
              <strong>{t('shell.languageCatalog')}</strong>
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={t('shell.close')}
                onClick={() => setOpen(false)}
              >
                <Icon name="close" size={18} />
              </button>
            </div>
          ) : null}
          <input
            className="lang-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('shell.searchLanguages')}
            autoFocus
          />
          {isLoading && <p className="muted">{t('shell.loadingCatalog')}</p>}
          {isError && <p className="warn">{t('shell.catalogFallback')}</p>}
          <ul className="lang-list" role="listbox">
            {filtered.map((l) => {
              const selected = baseCode(l.code) === baseCode(catalogCode || locale)
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    className={selected ? 'active' : undefined}
                    onClick={() => {
                      setLocale(l.code)
                      setOpen(false)
                    }}
                  >
                    <span className="lang-row-main">
                      <span className="endonym">{l.name_native}</span>
                      {l.region ? <span className="lang-region">{l.region}</span> : null}
                    </span>
                    <span className="lang-row-meta">
                      <span className="lang-code">{baseCode(l.code).toUpperCase()}</span>
                      {l.ui_tier === 'beta' || !l.ui_pack ? (
                        <span className="lang-beta">{t('shell.uiBeta')}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
